import React, { useEffect, useMemo, useState } from 'react';
import AvatarUser from '../../../components/AvatarUser';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { useAppContext } from '../../../contexts/AppContext';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { formatPhone } from '../../clientes/helpers';
import { UserService } from '../../configuracoes/usuario/Service';
import { SettingsRow, SettingsSection } from '../../configuracoes/estabelecimento/Form/SettingsGroup';
import GruposClientesService from '../../grupos-clientes/Service';
import { normalizeSearch } from '../../recorrencias/helpers';
import { useLembretesContext } from '../Context';
import LembretesService from '../Service';
import { Check, Search } from 'lucide-react';

const channelOptions = [
    { value: 'WHATSAPP', label: 'WhatsApp', description: 'Com o botão Agendar agora.' },
    { value: 'PUSH', label: 'Push', description: 'Notificação no aplicativo.' },
    { value: 'EMAIL', label: 'E-mail', description: 'Mensagem com link para o aplicativo.' },
];

const CLIENTS_VISIBLE_LIMIT = 30;

const resolveClientLabel = (client: any) =>
    String(client?.displayName || '').trim() ||
    String(client?.email || '').trim() ||
    formatPhone(client?.phoneNumber) ||
    'Cliente';

const userService = new UserService();

export default function LembretesForm() {
    const [loading, setLoading] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [clients, setClients] = useState<any[]>([]);
    const [groups, setGroups] = useState<any[]>([]);

    const { clear, id } = useParamsContext();
    const { reloadData, data } = useLembretesContext();
    const { successSnackbar, errorSnackbar } = useAppContext();

    const [dayOfMonth, setDayOfMonth] = useState<number | ''>(20);
    const [sendTime, setSendTime] = useState('09:00');
    const [triggerType, setTriggerType] = useState('MONTHLY');
    const [daysAfterCompletion, setDaysAfterCompletion] = useState<number | ''>(20);
    const [channels, setChannels] = useState<string[]>(['WHATSAPP']);
    const [targetClientIds, setTargetClientIds] = useState<string[]>([]);
    const [targetGroupIds, setTargetGroupIds] = useState<string[]>([]);
    const [clientSearch, setClientSearch] = useState('');
    const [audienceTab, setAudienceTab] = useState<'clients' | 'groups'>('clients');
    const doc = data?.find((item) => item._id === id);
    const docId = doc?._id;

    // Preenche ao abrir: recarregar a lista nao pode apagar o que foi digitado.
    useEffect(() => {
        if (!docId) return;

        setDayOfMonth(Number(doc.dayOfMonth || 20));
        setSendTime(doc.sendTime || '09:00');
        setTriggerType(doc.triggerType || 'MONTHLY');
        setDaysAfterCompletion(Number(doc.daysAfterCompletion || 20));
        setChannels(Array.isArray(doc.channels) ? doc.channels : []);
        setTargetClientIds(Array.isArray(doc.targetClientIds) ? doc.targetClientIds : []);
        setTargetGroupIds(Array.isArray(doc.targetGroupIds) ? doc.targetGroupIds : []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docId]);

    useEffect(() => {
        const loadOptions = async () => {
            try {
                setLoadingOptions(true);
                const [clientsResponse, groupsResponse] = await Promise.all([
                    userService.getClients(),
                    GruposClientesService.findAll(),
                ]);

                setClients(clientsResponse?.data || []);
                setGroups(groupsResponse?.data || []);
            } catch (e) {
                errorSnackbar('Ocorreu um erro ao carregar clientes e grupos.');
            } finally {
                setLoadingOptions(false);
            }
        };

        void loadOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedClientIdsSet = useMemo(() => new Set(targetClientIds), [targetClientIds]);

    const filteredClients = useMemo(() => {
        const search = normalizeSearch(clientSearch);
        if (!search) return clients;

        return clients.filter((client) =>
            normalizeSearch([client?.displayName, client?.email, client?.phoneNumber].filter(Boolean).join(' ')).includes(search),
        );
    }, [clients, clientSearch]);

    const visibleClients = useMemo(() => {
        // Mantem os selecionados visiveis antes de aplicar o limite.
        const selected = filteredClients.filter((client) => selectedClientIdsSet.has(String(client?.uid || '')));
        const others = filteredClients.filter((client) => !selectedClientIdsSet.has(String(client?.uid || '')));

        return [...selected, ...others].slice(0, CLIENTS_VISIBLE_LIMIT);
    }, [filteredClients, selectedClientIdsSet]);

    const toggle = (list: string[], value: string) =>
        list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

    const selectFilteredClients = () => {
        const ids = filteredClients.map((client) => String(client?.uid || '')).filter(Boolean);
        setTargetClientIds((current) => Array.from(new Set([...current, ...ids])));
    };

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!channels.length) {
            errorSnackbar('Selecione ao menos um canal de envio.');
            return;
        }

        if (!targetClientIds.length && !targetGroupIds.length) {
            errorSnackbar('Selecione ao menos um cliente ou grupo.');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                triggerType,
                ...(triggerType === 'AFTER_COMPLETION' ? { daysAfterCompletion: Number(daysAfterCompletion) } : {}),
                dayOfMonth: Number(dayOfMonth) || 20,
                sendTime,
                channels,
                targetClientIds,
                targetGroupIds,
            };

            if (id) {
                await LembretesService.update(id, payload);
                successSnackbar('Lembrete editado com sucesso.');
            } else {
                await LembretesService.create(payload);
                successSnackbar('Lembrete criado com sucesso.');
            }

            await reloadData();
            clear();
        } catch (e: any) {
            errorSnackbar(e?.response?.data?.message || 'Ocorreu um erro ao salvar o lembrete.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            await LembretesService.delete(id);
            await reloadData();
            successSnackbar('Lembrete removido.');
            clear();
        } catch (e) {
            errorSnackbar('Ocorreu um erro ao remover o lembrete.');
            setDeleting(false);
            setOpenDelete(false);
        }
    };

    return (
        <form onSubmit={submit} className="estab-settings sf-page px-2">
            <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
                <div className="space-y-6">
                    <SettingsSection
                        title="Quando enviar"
                        footer={
                            triggerType === 'MONTHLY'
                                ? 'Se o dia não existir no mês, o envio ocorre no último dia.'
                                : 'Conta a partir do atendimento concluído mais recente do cliente.'
                        }
                    >
                        <SettingsRow stacked>
                            <div className="sf-segmented" role="group" aria-label="Tipo de disparo">
                                <button
                                    type="button"
                                    aria-pressed={triggerType === 'MONTHLY'}
                                    onClick={() => setTriggerType('MONTHLY')}
                                >
                                    Dia fixo do mês
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={triggerType === 'AFTER_COMPLETION'}
                                    onClick={() => setTriggerType('AFTER_COMPLETION')}
                                >
                                    Após atendimento
                                </button>
                            </div>
                        </SettingsRow>
                        {triggerType === 'AFTER_COMPLETION' ? (
                            <SettingsRow label="Dias depois" htmlFor="lembrete-dias">
                                <input
                                    id="lembrete-dias"
                                    type="number"
                                    min={1}
                                    max={365}
                                    className="input estab-field"
                                    value={daysAfterCompletion}
                                    onChange={(e) => setDaysAfterCompletion(e.target.value === '' ? '' : Number(e.target.value))}
                                    required
                                />
                            </SettingsRow>
                        ) : (
                            <SettingsRow label="Dia do mês" htmlFor="lembrete-dia">
                                <input
                                    id="lembrete-dia"
                                    type="number"
                                    min={1}
                                    max={31}
                                    className="input estab-field"
                                    value={dayOfMonth}
                                    onChange={(e) => setDayOfMonth(e.target.value === '' ? '' : Number(e.target.value))}
                                    required
                                />
                            </SettingsRow>
                        )}
                        <SettingsRow label="Horário" htmlFor="lembrete-horario">
                            <input
                                id="lembrete-horario"
                                type="time"
                                className="input estab-field"
                                value={sendTime}
                                onChange={(e) => setSendTime(e.target.value)}
                                required
                            />
                        </SettingsRow>
                    </SettingsSection>

                    <SettingsSection title="Canais">
                        {channelOptions.map((channel) => (
                            <SettingsRow
                                key={channel.value}
                                label={channel.label}
                                htmlFor={`lembrete-canal-${channel.value}`}
                                description={channel.description}
                                inline
                            >
                                <input
                                    id={`lembrete-canal-${channel.value}`}
                                    type="checkbox"
                                    className="toggle toggle-success"
                                    checked={channels.includes(channel.value)}
                                    onChange={() => setChannels((current) => toggle(current, channel.value))}
                                />
                            </SettingsRow>
                        ))}
                    </SettingsSection>

                    {!!id && (
                        <div className="sf-group">
                            <button type="button" className="sf-destructive-row" onClick={() => setOpenDelete(true)}>
                                Remover lembrete
                            </button>
                        </div>
                    )}
                </div>

                <section>
                    <h2 className="sf-group-title">Para quem enviar</h2>
                    <div className="sf-segmented mb-2" role="group" aria-label="Público">
                        <button type="button" aria-pressed={audienceTab === 'clients'} onClick={() => setAudienceTab('clients')}>
                            Clientes
                            <span className="sf-segmented-count">{targetClientIds.length}</span>
                        </button>
                        <button type="button" aria-pressed={audienceTab === 'groups'} onClick={() => setAudienceTab('groups')}>
                            Grupos
                            <span className="sf-segmented-count">{targetGroupIds.length}</span>
                        </button>
                    </div>

                    {loadingOptions ? (
                        <div className="sf-skeleton h-48" aria-busy="true" />
                    ) : audienceTab === 'groups' ? (
                        groups.length === 0 ? (
                            <div className="sf-state !py-8">
                                <h2>Nenhum grupo criado</h2>
                            </div>
                        ) : (
                            <ul className="sf-group">
                                {groups.map((group) => {
                                    const selected = targetGroupIds.includes(group._id);

                                    return (
                                        <li key={group._id}>
                                            <button
                                                type="button"
                                                className="sf-row"
                                                aria-pressed={selected}
                                                onClick={() => setTargetGroupIds((current) => toggle(current, group._id))}
                                            >
                                                <span className="sf-row-body">
                                                    <span className="sf-row-title !font-medium">{group.name}</span>
                                                    {!!group.description && <span className="sf-row-sub">{group.description}</span>}
                                                </span>
                                                {selected && <Check className="sf-row-check" aria-hidden />}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )
                    ) : (
                        <>
                            <label className="sf-search mb-2">
                                <Search aria-hidden />
                                <input
                                    type="search"
                                    placeholder="Buscar cliente"
                                    aria-label="Buscar cliente"
                                    value={clientSearch}
                                    onChange={(e) => setClientSearch(e.target.value)}
                                />
                            </label>
                            <div className="mb-1 flex justify-between gap-2 px-1">
                                <button
                                    type="button"
                                    className="sf-link !text-xs"
                                    disabled={!filteredClients.length}
                                    onClick={selectFilteredClients}
                                >
                                    Selecionar {clientSearch ? 'resultados' : 'todos'} ({filteredClients.length})
                                </button>
                                <button
                                    type="button"
                                    className="sf-link !text-xs"
                                    disabled={!targetClientIds.length}
                                    onClick={() => setTargetClientIds([])}
                                >
                                    Limpar seleção
                                </button>
                            </div>

                            {!filteredClients.length ? (
                                <div className="sf-state !py-8">
                                    <h2>Nenhum cliente encontrado</h2>
                                </div>
                            ) : (
                                <ul className="sf-group [--sf-row-inset:3.75rem]">
                                    {visibleClients.map((client) => {
                                        const clientId = String(client?.uid || '');
                                        const clientName = resolveClientLabel(client);
                                        const selected = selectedClientIdsSet.has(clientId);

                                        return (
                                            <li key={clientId}>
                                                <button
                                                    type="button"
                                                    className="sf-row"
                                                    aria-pressed={selected}
                                                    onClick={() => setTargetClientIds((current) => toggle(current, clientId))}
                                                >
                                                    <AvatarUser name={clientName} photoURL={client?.photoURL} size="h-9 w-9 !text-xs" />
                                                    <span className="sf-row-body">
                                                        <span className="sf-row-title !font-medium">{clientName}</span>
                                                        <span className="sf-row-sub">
                                                            {formatPhone(client?.phoneNumber) || client?.email || 'Sem contato'}
                                                        </span>
                                                    </span>
                                                    {selected && <Check className="sf-row-check" aria-hidden />}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}

                            {filteredClients.length > CLIENTS_VISIBLE_LIMIT && (
                                <p className="mt-2 px-1 text-xs text-base-content/55">
                                    Mostrando {CLIENTS_VISIBLE_LIMIT} de {filteredClients.length}. Use a busca para refinar.
                                </p>
                            )}
                        </>
                    )}
                </section>
            </div>

            <div className="estab-savebar mt-6 gap-2">
                <button
                    type="button"
                    className="btn btn-ghost flex-1 sm:flex-none sm:min-w-[8rem]"
                    onClick={clear}
                    disabled={loading}
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    className="btn btn-primary flex-1 sm:flex-none sm:min-w-[11rem]"
                    disabled={loading}
                >
                    {loading && <span className="loading loading-spinner loading-sm"></span>}
                    {id ? 'Salvar alterações' : 'Criar lembrete'}
                </button>
            </div>

            {openDelete && (
                <ConfirmDialog
                    title="Remover lembrete?"
                    confirmLabel="Remover"
                    loading={deleting}
                    onConfirm={handleDelete}
                    onClose={() => setOpenDelete(false)}
                >
                    Os clientes deixam de receber este lembrete. Envios já feitos não são afetados.
                </ConfirmDialog>
            )}
        </form>
    );
}
